import React from 'react';
import { Alert } from 'antd';
import { ClientStatus } from '../../Constants/enums';

/**
 * Actor-facing onboarding helper for the Customer Detail page.
 * The enterprise onboarding flow is long, so this reads the customer's current
 * ClientStatus and shows one plain, easy-to-read line: where they are in the
 * flow and the single next action (naming the exact on-page button).
 * Source of truth: VelorHR-Administration-Frontend-Canonical-Flow.md.
 */

// Map a ClientStatus to { tone, heading, next }. `heading` states where the
// customer is (step N of 6); `next` is the one action to take now.
const guideFor = (status) => {
  switch (status) {
    case ClientStatus.Onboarding:
    case ClientStatus.Prospect:
    case ClientStatus.InformationRequested:
      return {
        tone: 'info',
        heading: 'Awaiting the onboarding form',
        next: 'Waiting for the customer to complete the public onboarding form. Their details appear here once they submit.',
      };
    case ClientStatus.InformationSubmitted:
      return {
        tone: 'info',
        heading: 'Step 1 of 6 · Information submitted',
        next: 'Review the Organization Profile and Contacts below, then click "Move to Commercial Review" to begin commercial setup.',
      };
    case ClientStatus.CommercialReview:
      return {
        tone: 'info',
        heading: 'Step 2 of 6 · Commercial review',
        next: 'Set the plan, seats, and billing cycle with "Set Commercial Terms". When payment is required, the customer moves to Payment Pending.',
      };
    case ClientStatus.PaymentPending:
      return {
        tone: 'warning',
        heading: 'Step 3 of 6 · Payment pending',
        next: 'Open "Invoices" to create and send the invoice. The customer pays via the Stripe link, and the status advances automatically once payment is confirmed.',
      };
    case ClientStatus.Provisioning:
      return {
        tone: 'info',
        heading: 'Step 4 of 6 · Provisioning',
        next: 'Payment received. Use "Rotate Integration Key" then "Configure Deployment". Mark the deployment Ready only when the app, API, and databases are reachable.',
      };
    case ClientStatus.Ready:
      return {
        tone: 'info',
        heading: 'Step 5 of 6 · Ready to activate',
        next: 'Deployment is Ready and the customer app is provisioning the Organization Admin (see below). Once it shows Activated, click "Activate Customer" to go live.',
      };
    case ClientStatus.Active:
      return {
        tone: 'success',
        heading: 'Onboarding complete',
        next: 'This customer is live. Nothing further to do here.',
      };
    case ClientStatus.Suspended:
      return {
        tone: 'error',
        heading: 'Customer suspended',
        next: 'This customer is suspended. Click "Reactivate" to restore access.',
      };
    default:
      return null;
  }
};

const OnboardingGuide = ({ status }) => {
  if (status === null || status === undefined) return null;
  const g = guideFor(status);
  if (!g) return null;
  return (
    <Alert
      style={{ marginBottom: 16 }}
      type={g.tone}
      showIcon
      message={g.heading}
      description={g.next}
    />
  );
};

export default OnboardingGuide;
